'use client'

import { useMemo, useState } from 'react'
import { useCart } from './useCart'
import { useCartActions } from './useCartActions'
import { isProductInStock } from '@/utils/util'
import { colorSwatches, colorVariant, defaultColor, getOptions } from '../variantOptions'

export function useProductCard(product: any, vendor: any, lockVariant: boolean = false) {
  const { loading } = useCart()
  const { addToCartWithRedirect } = useCartActions(vendor)
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [pickedColor, setPickedColor] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const colors = useMemo(() => {
    const swatches = lockVariant ? [] : colorSwatches(product)
    return swatches.length > 1 ? swatches : []
  }, [product, lockVariant])
  const activeColor = colors.some(c => c.value === pickedColor) ? pickedColor : defaultColor(product, colors)
  const colorSoldOut = !!activeColor && !!colors.find(c => c.value === activeColor)?.soldOut

  const isProductAvailable = isProductInStock(product) && !colorSoldOut
  const availableVariant = (activeColor && colorVariant(product, activeColor)) || product?.variant || (product?.variants && product.variants.length > 0 ? product.variants[0] : null)
  const cardProduct = useMemo(() => activeColor ? { ...product, variant: availableVariant } : product, [product, activeColor, availableVariant])

  const add = async (variantId: string) => {
    setAdding(true)
    try {
      await addToCartWithRedirect({ productId: product.id, quantity: 1, variantId })
    } finally {
      setAdding(false)
    }
  }

  const handleAddToCart = async (e?: React.MouseEvent, variantId?: string) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (variantId) {
      await add(variantId)
      return
    }
    if (activeColor && availableVariant?.id && getOptions(product).length === 1) {
      await add(availableVariant.id)
      return
    }
    const hasMultipleVariants = product?.variants && product.variants.length > 1
    if (hasMultipleVariants) {
      setShowVariantModal(true)
      return
    }
    const variant = product?.variant || availableVariant
    if (!variant?.id) return
    await add(variant.id)
  }

  const handleVariantAddToCart = (variantId: string) => {
    handleAddToCart(undefined, variantId)
  }

  return {
    isProductAvailable,
    availableVariant,
    loading, adding,
    showVariantModal, setShowVariantModal,
    handleAddToCart, handleVariantAddToCart,
    colors, activeColor, selectColor: setPickedColor,
    cardProduct,
  }
}
